import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  Users, 
  Search, 
  ChevronLeft,
  MoreVertical,
  Mail,
  Phone,
  Building2,
  Shield,
  UserX,
  UserCheck,
  RefreshCw,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SuperAdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch companies first
      const companiesRes = await axios.get(`${API_BASE_URL}/companies`, { headers });
      setCompanies(companiesRes.data.companies || []);

      // Fetch users from all companies
      const allUsers = [];
      for (const company of companiesRes.data.companies || []) {
        try {
          const usersRes = await axios.get(
            `${API_BASE_URL}/super-admin/companies/${company.id}/users`,
            { headers }
          );
          const companyUsers = (usersRes.data.users || []).map(u => ({
            ...u,
            company_name: company.name,
            company_id: company.id
          }));
          allUsers.push(...companyUsers);
        } catch (err) {
          console.error(`Failed to fetch users for ${company.name}:`, err);
        }
      }
      setUsers(allUsers);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = !user.is_active;
      
      await axios.put(
        `${API_BASE_URL}/super-admin/users/${user.id}`,
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, is_active: newStatus } : u
      ));
      
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesCompany = filterCompany === 'all' || user.company_id === filterCompany;
    
    return matchesSearch && matchesCompany;
  });

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDialog(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/super-admin')}
          className="text-wt-text-secondary hover:text-wt-text-primary"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Platform Users</h1>
          <p className="text-wt-text-secondary">
            {users.length} users across {companies.length} companies
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-secondary" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-wt-border text-wt-text-primary">
                  <Filter className="h-4 w-4 mr-2" />
                  {filterCompany === 'all' ? 'All Companies' : 
                    companies.find(c => c.id === filterCompany)?.name || 'All'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-wt-surface border-wt-border">
                <DropdownMenuItem
                  onClick={() => setFilterCompany('all')}
                  className="text-wt-text-primary"
                >
                  All Companies
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-wt-border" />
                {companies.map(company => (
                  <DropdownMenuItem
                    key={company.id}
                    onClick={() => setFilterCompany(company.id)}
                    className="text-wt-text-primary"
                  >
                    {company.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-wt-border text-wt-text-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            <Users className="h-5 w-5 text-wt-gold" />
            Users ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-wt-text-secondary">
              No users found matching your criteria
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border hover:border-wt-gold/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-wt-gold/10 flex items-center justify-center">
                      <span className="text-wt-gold font-bold text-lg">
                        {(user.full_name || user.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-wt-text-primary">
                          {user.full_name || 'No name'}
                        </p>
                        {user.is_super_admin && (
                          <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                            <Shield className="h-3 w-3 mr-1" />
                            Super Admin
                          </Badge>
                        )}
                        {user.is_admin && !user.is_super_admin && (
                          <Badge className="bg-wt-gold/10 text-wt-gold border-wt-gold/20">
                            Admin
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-wt-text-secondary">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </span>
                        {user.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {user.phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-wt-text-secondary mt-1">
                        <Building2 className="h-3 w-3" />
                        {user.company_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={user.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-wt-text-secondary">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-wt-surface border-wt-border">
                        <DropdownMenuItem 
                          onClick={() => openUserDetails(user)}
                          className="text-wt-text-primary"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-wt-border" />
                        <DropdownMenuItem 
                          onClick={() => handleToggleUserStatus(user)}
                          className={user.is_active ? 'text-red-400' : 'text-green-400'}
                        >
                          {user.is_active ? (
                            <>
                              <UserX className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-wt-surface border-wt-border sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Users className="h-5 w-5 text-wt-gold" />
              User Details
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-wt-gold/10 flex items-center justify-center">
                  <span className="text-wt-gold font-bold text-2xl">
                    {(selectedUser.full_name || selectedUser.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-lg font-medium text-wt-text-primary">
                    {selectedUser.full_name || 'No name'}
                  </p>
                  <div className="flex gap-2 mt-1">
                    {selectedUser.is_super_admin && (
                      <Badge className="bg-purple-500/10 text-purple-400">Super Admin</Badge>
                    )}
                    {selectedUser.is_admin && (
                      <Badge className="bg-wt-gold/10 text-wt-gold">Admin</Badge>
                    )}
                    <Badge className={selectedUser.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-wt-border">
                <div>
                  <p className="text-xs text-wt-text-secondary">Email</p>
                  <p className="text-sm text-wt-text-primary">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-secondary">Phone</p>
                  <p className="text-sm text-wt-text-primary">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-secondary">Company</p>
                  <p className="text-sm text-wt-text-primary">{selectedUser.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-secondary">Role</p>
                  <p className="text-sm text-wt-text-primary">
                    {selectedUser.role || selectedUser.custom_role_name || 'User'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-secondary">User ID</p>
                  <p className="text-sm text-wt-text-primary font-mono text-xs">{selectedUser.id}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-secondary">Company ID</p>
                  <p className="text-sm text-wt-text-primary font-mono text-xs">{selectedUser.company_id}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUserDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Close
            </Button>
            {selectedUser && (
              <Button
                onClick={() => {
                  handleToggleUserStatus(selectedUser);
                  setShowUserDialog(false);
                }}
                className={selectedUser.is_active 
                  ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                }
              >
                {selectedUser.is_active ? (
                  <>
                    <UserX className="h-4 w-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="h-4 w-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminUsersPage;
